<template>
  <div class="sc-wrap">

    <!-- Nav row -->
    <div class="sc-wnav-row">

      <!-- Left: prev / label or range / next -->
      <button v-if="viewMode !== 'C'" class="sc-wbtn" @click="prev">&#8249;</button>

      <div class="sc-nav-label-wrap">
        <!-- Range selecting state -->
        <template v-if="rangeStart && viewMode !== 'C' && viewMode !== 'Q' && viewMode !== 'Y' && viewMode !== 'D'">
          <span class="sc-range-pill sc-range-pill--start">{{ fmtShort(rangeStart) }}</span>
          <span class="sc-range-arrow">→</span>
          <span class="sc-range-pill" :class="rangeEnd ? 'sc-range-pill--end' : 'sc-range-pill--empty'">{{ rangeEnd ? fmtShort(rangeEnd) : '?' }}</span>
          <button v-if="rangeEnd" class="sc-range-x" @click.stop="clearRange">✕</button>
        </template>
        <!-- Normal label -->
        <template v-else>
          <span class="sc-wlabel">{{ viewMode === 'C' ? 'Custom' : navLabel }}</span>
        </template>
      </div>

      <button v-if="viewMode !== 'C'" class="sc-wbtn" @click="next">&#8250;</button>

      <!-- Right: view select + refresh -->
      <div class="sc-nav-right">
        <!-- Custom date inputs inline -->
        <template v-if="viewMode === 'C'">
          <input type="date" class="sc-date-input" v-model="customFrom" @change="loadData" />
          <span class="sc-range-sep">–</span>
          <input type="date" class="sc-date-input" v-model="customTo" @change="loadData" />
          <button class="sc-range-apply" @click="loadData">Go</button>
        </template>
        <div class="sc-view-wrap">
          <div ref="scViewBtn" class="sc-view-btn" :class="{open: scViewOpen}" @click.stop="toggleViewDd">
            <span>{{ viewModes.find(v => v.k === viewMode).l }}</span>
            <svg class="sc-view-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <transition name="sc-dd-fade">
            <div v-if="scViewOpen" class="sc-view-dd" :style="scViewDdStyle">
              <div v-for="v in viewModes" :key="v.k"
                   class="sc-view-opt" :class="{on: viewMode === v.k}"
                   @click.stop="setViewMode(v.k); scViewOpen = false">
                <span>{{ v.l }}</span>
                <svg v-if="viewMode === v.k" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </div>
          </transition>
        </div>
        <button class="sc-refresh-btn" @click="resetAll" :class="{ spin: gridLoading }" title="Clear all filters">
          <svg viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
        </button>
      </div>

    </div>

    <!-- ===== DAY VIEW ===== -->
    <div v-if="viewMode === 'D'" class="sc-day-view" :class="{ loading: gridLoading }">
      <div class="sc-day-summary">
        <div v-for="chip in activeChips" :key="chip.key" class="sc-day-kpi">
          <div class="sc-day-kpi-val" :style="{ color: chip.color }">{{ dayCount(dayStart.format('YYYY-MM-DD'), chip.key) }}</div>
          <div class="sc-day-kpi-lbl">{{ chip.label }}</div>
        </div>
      </div>
    </div>

    <!-- ===== WEEK VIEW ===== -->
    <div v-else-if="viewMode === 'W'" class="sc-grid" :class="{ loading: gridLoading }">
      <div v-for="day in weekDays" :key="day.date" class="sc-day"
        :class="{
          today:        day.isToday,
          selected:     !rangeEnd && selectedDate === day.date,
          'sc-rs':      day.date === rangeStart,
          'sc-re':      day.date === rangeEnd,
          'sc-in-range': isInRange(day.date)
        }"
        @click="selectDay(day.date)">
        <div class="sc-day-hdr">
          <span class="sc-dow">{{ day.dow }}</span>
          <span class="sc-dd" :class="{ 'sc-dd-today': day.isToday }">{{ day.dd }}</span>
        </div>
        <div class="sc-day-cnts">
          <div v-for="chip in activeChips" :key="chip.key"
            v-if="dayCount(day.date, chip.key) > 0"
            class="sc-cnt-row"
            :title="chip.label + ': ' + dayCount(day.date, chip.key)"
            @click.stop="filterByChip(day.date, chip)">
            <span class="sc-cnt-dot" :style="{ background: chip.color }"></span>
            <span class="sc-cnt-num" :style="{ color: chip.color }">{{ dayCount(day.date, chip.key) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== MONTH VIEW ===== -->
    <div v-else-if="viewMode === 'M'" class="sc-mgrid" :class="{ loading: gridLoading }">
      <div class="sc-mhdr" v-for="h in ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']" :key="h">{{ h }}</div>
      <div v-for="(cell, i) in monthCells" :key="'mc' + i" class="sc-mcell"
        :class="{
          'sc-mcell-empty':  !cell,
          'sc-mcell-today':  cell && cell.isToday,
          'sc-mcell-sel':    cell && !rangeEnd && selectedDate === cell.date,
          'sc-rs':           cell && cell.date === rangeStart,
          'sc-re':           cell && cell.date === rangeEnd,
          'sc-in-range':     cell && isInRange(cell.date)
        }"
        @click="cell ? selectDay(cell.date) : null">
        <template v-if="cell">
          <span class="sc-mdd" :class="{ 'sc-mdd-today': cell.isToday }">{{ cell.dd }}</span>
          <div class="sc-mdots">
            <div v-for="chip in activeChips" :key="chip.key"
              v-if="dayCount(cell.date, chip.key) > 0"
              class="sc-mdot"
              :style="{ background: chip.color }"
              :title="chip.label + ': ' + dayCount(cell.date, chip.key)"
              @click.stop="filterByChip(cell.date, chip)"></div>
          </div>
          <div class="sc-mday-total" v-if="dayTotal(cell.date) > 0">{{ dayTotal(cell.date) }}</div>
        </template>
      </div>
    </div>

    <!-- ===== QUARTER VIEW — 4 boxes ===== -->
    <div v-else-if="viewMode === 'Q'" class="sc-q-grid" :class="{ loading: gridLoading }">
      <div v-for="q in fourQuarters" :key="q.label" class="sc-q-box" @click="drillToMonth(q.start)">
        <div class="sc-q-top">
          <span class="sc-q-label">{{ q.label }}</span>
          <span class="sc-q-range">{{ q.range }}</span>
        </div>
        <div class="sc-q-bars">
          <div v-for="chip in activeChips" :key="chip.key"
            v-if="q.counts[chip.key] > 0"
            class="sc-q-bar"
            :style="{ background: chip.color, width: aggPct(q.counts[chip.key], maxQCount) + '%' }">
            <span class="sc-agg-val">{{ q.counts[chip.key] }}</span>
          </div>
          <div v-if="!activeChips.some(c => q.counts[c.key] > 0)" class="sc-q-empty">No data</div>
        </div>
        <div class="sc-q-total">{{ q.total }}</div>
      </div>
    </div>

    <!-- ===== YEAR VIEW ===== -->
    <div v-else-if="viewMode === 'Y'" class="sc-agg-list" :class="{ loading: gridLoading }">
      <div v-for="yr in recentYears" :key="yr.label" class="sc-agg-row" @click="drillToQuarterYear(yr.year)">
        <span class="sc-agg-lbl">{{ yr.label }}</span>
        <div class="sc-agg-bars">
          <div v-for="chip in activeChips" :key="chip.key"
            v-if="yr.counts[chip.key] > 0"
            class="sc-agg-bar"
            :style="{ background: chip.color, width: aggPct(yr.counts[chip.key], maxYCount) + '%' }">
            <span class="sc-agg-val">{{ yr.counts[chip.key] }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CUSTOM VIEW ===== -->
    <div v-else-if="viewMode === 'C'" class="sc-agg-list" :class="{ loading: gridLoading }">
      <div v-for="(row, i) in customRangeDays" :key="'cr' + i" class="sc-agg-row" @click="selectDay(row.date)">
        <span class="sc-agg-lbl">{{ row.label }}</span>
        <div class="sc-agg-bars">
          <div v-for="chip in activeChips" :key="chip.key"
            v-if="dayCount(row.date, chip.key) > 0"
            class="sc-agg-bar"
            :style="{ background: chip.color, width: aggPct(dayCount(row.date, chip.key), maxCCount) + '%' }">
            <span class="sc-agg-val">{{ dayCount(row.date, chip.key) }}</span>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import moment from "moment/moment";
import apiClientCreator from "../../helpers/api/request";

const CHIPS = [
  { key: "DP",  label: "DP",         color: "#7C5CFC", endpoint: "dep/days",      fromParam: "deposit_at_from",        toParam: "deposit_at_to",          hoursEndpoint: "dep" },
  { key: "SC",  label: "SC",         color: "#E8891C", endpoint: "sc/days",       fromParam: "scheduled_call_to_from", toParam: "scheduled_call_to_to",    hoursEndpoint: "ca_f" },
  { key: "NA",  label: "No Answer",  color: "#E05A3A", endpoint: "na_tag/days",   fromParam: "last_modified_na_from",  toParam: "last_modified_na_to",     hoursEndpoint: "na_tag" },
  { key: "LD",  label: "Lead",       color: "#D4A017", endpoint: "lead_tag/days", fromParam: "last_modified_lead_from",toParam: "last_modified_lead_to",   hoursEndpoint: "lead_tag" },
  { key: "REG", label: "REG",        color: "#2D9D5E", endpoint: "reg/days",      fromParam: "registered_on_from",     toParam: "registered_on_to",        hoursEndpoint: "reg" },
  { key: "NI",  label: "NI",         color: "#9A6F00", endpoint: "ni_tag/days",   fromParam: "last_modified_ni_from",  toParam: "last_modified_ni_to",     hoursEndpoint: "ni_tag" },
  { key: "BN",  label: "Bad Number", color: "#6B7280", endpoint: "bn_tag/days",   fromParam: "last_modified_bn_from",  toParam: "last_modified_bn_to",     hoursEndpoint: "bn_tag" },
];

const YEAR_RANGE = 6;

export default {
  name: "C1SmartCalendar",
  props: { filterParams: { type: Object, default: () => ({}) } },

  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      scViewOpen: false,
      scViewDdStyle: {},
      viewMode: "W",
      viewModes: [
        { k: "W", l: "Week" },
        { k: "M", l: "Month" },
        { k: "Q", l: "Quarter" },
        { k: "Y", l: "Year" },
        { k: "C", l: "Custom" },
      ],
      dayStart:     moment(),
      weekStart:    moment().startOf("isoWeek"),
      monthStart:   moment().startOf("month"),
      quarterYear:  moment().year(),
      customFrom:   moment().subtract(7, "days").format("YYYY-MM-DD"),
      customTo:     today,
      dayStats:     {},
      hourStats:    {},
      selectedDate: today,
      selectedHourType: "NA",
      rangeStart:   null,
      rangeEnd:     null,
      visibleTypes: ["DP", "SC", "NA", "LD", "REG", "NI", "BN"],
      gridLoading:  false,
      hoursLoading: false,
      chips: CHIPS,
    };
  },

  watch: {
    filterParams: {
      deep: true,
      handler(val) {
        // Sync visible types to whatever status filter is active
        const all = ["DP", "SC", "NA", "LD", "REG", "NI", "BN"];
        this.visibleTypes = (val && val.activeTypes && val.activeTypes.length) ? val.activeTypes : all;
        this.loadData();
      }
    }
  },
  computed: {
    viewRange() {
      switch (this.viewMode) {
        case "D": return { from: this.dayStart.clone().startOf("day"),     to: this.dayStart.clone().endOf("day") };
        case "W": return { from: this.weekStart.clone(),                   to: this.weekStart.clone().endOf("isoWeek") };
        case "M": return { from: this.monthStart.clone().startOf("month"), to: this.monthStart.clone().endOf("month") };
        case "Q": return { from: moment().year(this.quarterYear).startOf("year"), to: moment().year(this.quarterYear).endOf("year") };
        case "Y": return { from: moment().subtract(YEAR_RANGE - 1, "years").startOf("year"), to: moment().endOf("year") };
        case "C": return { from: moment(this.customFrom), to: moment(this.customTo) };
        default:  return { from: moment(), to: moment() };
      }
    },

    navLabel() {
      switch (this.viewMode) {
        case "D": return this.dayStart.format("ddd, DD MMM YYYY");
        case "W": return this.weekStart.format("DD MMM") + " – " + this.weekStart.clone().endOf("isoWeek").format("DD MMM YYYY");
        case "M": return this.monthStart.format("MMMM YYYY");
        case "Q": return String(this.quarterYear);
        case "Y": return (moment().year() - YEAR_RANGE + 1) + " – " + moment().year();
      }
    },

    selectedDateFmt() { return this.selectedDate ? moment(this.selectedDate).format("ddd, DD MMM") : ""; },

    weekDays() {
      const today = moment().format("YYYY-MM-DD");
      return Array.from({ length: 7 }, (_, i) => {
        const d = this.weekStart.clone().add(i, "days");
        const date = d.format("YYYY-MM-DD");
        return { date, dow: d.format("ddd").toUpperCase(), dd: d.format("D"), isToday: date === today };
      });
    },

    monthCells() {
      const cells = [];
      const start = this.monthStart.clone().startOf("month");
      const end   = start.clone().endOf("month");
      const today = moment().format("YYYY-MM-DD");
      const offset = start.isoWeekday() - 1;
      for (let i = 0; i < offset; i++) cells.push(null);
      let d = start.clone();
      while (d.isSameOrBefore(end, "day")) {
        const date = d.format("YYYY-MM-DD");
        cells.push({ date, dd: d.date(), isToday: date === today });
        d.add(1, "day");
      }
      while (cells.length % 7 !== 0) cells.push(null);
      return cells;
    },

    fourQuarters() {
      return [1, 2, 3, 4].map(q => {
        const qStart = moment().year(this.quarterYear).quarter(q).startOf("quarter");
        const qEnd   = qStart.clone().endOf("quarter");
        const counts = {};
        CHIPS.forEach(c => { counts[c.key] = 0; });
        let d = qStart.clone();
        while (d.isSameOrBefore(qEnd, "day")) {
          const date = d.format("YYYY-MM-DD");
          if (this.dayStats[date]) CHIPS.forEach(c => { counts[c.key] += this.dayStats[date][c.key] || 0; });
          d.add(1, "day");
        }
        const total = CHIPS.reduce((s, c) => s + counts[c.key], 0);
        return { label: "Q" + q, range: qStart.format("MMM") + "–" + qEnd.format("MMM"), start: qStart.format("YYYY-MM-DD"), counts, total };
      });
    },

    maxQCount() {
      let max = 1;
      this.fourQuarters.forEach(q => this.activeChips.forEach(c => { if (q.counts[c.key] > max) max = q.counts[c.key]; }));
      return max;
    },

    recentYears() {
      const current = moment().year();
      return Array.from({ length: YEAR_RANGE }, (_, i) => {
        const y = current - i;
        const yStart = moment().year(y).startOf("year");
        const yEnd   = yStart.clone().endOf("year");
        const counts = {};
        CHIPS.forEach(c => { counts[c.key] = 0; });
        let d = yStart.clone();
        const cap = moment();
        while (d.isSameOrBefore(yEnd, "day") && d.isSameOrBefore(cap, "day")) {
          const date = d.format("YYYY-MM-DD");
          if (this.dayStats[date]) CHIPS.forEach(c => { counts[c.key] += this.dayStats[date][c.key] || 0; });
          d.add(1, "day");
        }
        return { label: String(y), year: y, start: yStart.format("YYYY-MM-DD"), counts };
      });
    },

    maxYCount() {
      let max = 1;
      this.recentYears.forEach(yr => this.activeChips.forEach(c => { if (yr.counts[c.key] > max) max = yr.counts[c.key]; }));
      return max;
    },

    customRangeDays() {
      const days = [];
      const from = moment(this.customFrom);
      const to   = moment(this.customTo);
      if (!from.isValid() || !to.isValid()) return days;
      let d = from.clone();
      while (d.isSameOrBefore(to, "day")) {
        days.push({ date: d.format("YYYY-MM-DD"), label: d.format("DD/MM ddd") });
        d.add(1, "day");
      }
      return days;
    },

    maxCCount() {
      let max = 1;
      this.customRangeDays.forEach(row => this.activeChips.forEach(c => { const v = this.dayCount(row.date, c.key); if (v > max) max = v; }));
      return max;
    },

    activeChips()    { return this.chips.filter(c => this.visibleTypes.includes(c.key)); },
    activeHourChip() { return this.chips.find(c => c.key === this.selectedHourType) || this.chips[0]; },
    displayHours()   { return Array.from({ length: 15 }, (_, i) => i + 8); },

    maxHourCount() {
      let max = 0;
      this.displayHours.forEach(h => { const k = String(h).padStart(2, "0"); if ((this.hourStats[k] || 0) > max) max = this.hourStats[k]; });
      return max || 1;
    },
  },

  mounted() {
    this._scViewClose = (e) => {
      if (this.scViewOpen && this.$refs.scViewBtn && !this.$refs.scViewBtn.contains(e.target)) {
        this.scViewOpen = false;
      }
    };
    document.addEventListener('click', this._scViewClose);
    // Load calendar data and visually select today (parent controls the initial fetch separately)
    this.loadData().then(() => {
      const today = moment().format('YYYY-MM-DD');
      this.selectedDate = today;
      this.rangeStart   = today;
      this.rangeEnd     = today;
      this.loadHours(today);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this._scViewClose);
  },

  methods: {
    toggleViewDd() {
      if (!this.scViewOpen) {
        const rect = this.$refs.scViewBtn.getBoundingClientRect();
        this.scViewDdStyle = {
          position: 'fixed',
          top: (rect.bottom + 4) + 'px',
          right: (window.innerWidth - rect.right) + 'px',
          zIndex: 9999,
        };
      }
      this.scViewOpen = !this.scViewOpen;
    },
    emitViewRange() {
      if (this.viewMode === 'C') return; // custom range: wait for user to pick both dates
      const from = this.viewRange.from.format('YYYY-MM-DD');
      const to   = this.viewRange.to.format('YYYY-MM-DD');
      this.$emit('daySelected', { date: from, dateTo: to, type: null, fromParam: null });
    },

    setViewMode(v) {
      this.viewMode = v;
      this.selectedDate = null;
      this.rangeStart = null;
      this.rangeEnd = null;
      this.hourStats = {};
      if (v === "D") { this.dayStart = moment(); this.selectedDate = this.dayStart.format("YYYY-MM-DD"); }
      this.loadData();
      if (v !== 'C') this.$nextTick(() => this.emitViewRange());
    },

    prev() {
      if      (this.viewMode === "D") this.dayStart   = this.dayStart.clone().subtract(1, "day");
      else if (this.viewMode === "W") this.weekStart  = this.weekStart.clone().subtract(7, "days");
      else if (this.viewMode === "M") this.monthStart = this.monthStart.clone().subtract(1, "month");
      else if (this.viewMode === "Q") this.quarterYear -= 1;
      else if (this.viewMode === "Y") return;
      // Only auto-emit if no range is being picked (rangeStart means user is mid-selection)
      if (!this.rangeStart) {
        this.clearRange();
        this.selectedDate = this.viewMode === "D" ? this.dayStart.format("YYYY-MM-DD") : null;
        this.loadData();
        if (this.viewMode !== 'C') this.$nextTick(() => this.emitViewRange());
      } else {
        // Mid-range picking: just navigate, don't clear start or emit
        this.loadData();
      }
    },

    next() {
      if      (this.viewMode === "D") this.dayStart   = this.dayStart.clone().add(1, "day");
      else if (this.viewMode === "W") this.weekStart  = this.weekStart.clone().add(7, "days");
      else if (this.viewMode === "M") this.monthStart = this.monthStart.clone().add(1, "month");
      else if (this.viewMode === "Q") this.quarterYear += 1;
      else if (this.viewMode === "Y") return;
      // Only auto-emit if no range is being picked
      if (!this.rangeStart) {
        this.clearRange();
        this.selectedDate = this.viewMode === "D" ? this.dayStart.format("YYYY-MM-DD") : null;
        this.loadData();
        if (this.viewMode !== 'C') this.$nextTick(() => this.emitViewRange());
      } else {
        this.loadData();
      }
    },

    drillToWeek(dateStr) {
      this.weekStart = moment(dateStr).startOf("isoWeek");
      this.viewMode = "W"; this.clearRange(); this.selectedDate = null;
      this.loadData();
    },

    drillToMonth(dateStr) {
      this.monthStart = moment(dateStr).startOf("month");
      this.viewMode = "M"; this.clearRange(); this.selectedDate = null;
      this.loadData();
    },

    drillToQuarterYear(year) {
      this.quarterYear = year;
      this.viewMode = "Q"; this.clearRange(); this.selectedDate = null;
      this.loadData();
    },

    // Range selection: 1st click = start, 2nd click = end, 3rd = reset
    selectDay(date) {
      if (!this.rangeStart) {
        // First click — set start
        this.rangeStart = date;
        this.rangeEnd   = null;
        this.selectedDate = date;
        this.loadHours(date);
      } else if (!this.rangeEnd) {
        // Second click — finalize range and auto-filter (same day = single day range)
        const a = date < this.rangeStart ? date : this.rangeStart;
        const b = date < this.rangeStart ? this.rangeStart : date;
        this.rangeStart   = a;
        this.rangeEnd     = b;
        this.selectedDate = null;
        this.hourStats    = {};
        this.$nextTick(() => this.applyRangeFilter());
      } else {
        // Third click — restart
        this.rangeStart   = date;
        this.rangeEnd     = null;
        this.selectedDate = date;
        this.loadHours(date);
      }
    },

    isInRange(date) {
      if (!this.rangeStart || !this.rangeEnd) return false;
      return date > this.rangeStart && date < this.rangeEnd;
    },

    clearRange() {
      const today = moment().format('YYYY-MM-DD');
      this.rangeStart   = today;
      this.rangeEnd     = today;
      this.selectedDate = today;
      this.hourStats    = {};
    },

    applyRangeFilter() {
      if (!this.rangeStart || !this.rangeEnd) return;
      const chip = this.activeHourChip;
      this.$emit("daySelected", { date: this.rangeStart, dateTo: this.rangeEnd, type: chip.key, fromParam: chip.fromParam, toParam: chip.toParam });
    },

    fmtShort(date) { return moment(date).format("DD MMM"); },

    toggleType(key) {
      const idx = this.visibleTypes.indexOf(key);
      if (idx === -1) this.visibleTypes.push(key);
      else if (this.visibleTypes.length > 1) this.visibleTypes.splice(idx, 1);
    },

    filterByChip(date, chip) { this.$emit("daySelected", { date, type: chip.key, fromParam: chip.fromParam, toParam: chip.toParam }); },

    resetAll() {
      const today = moment().format('YYYY-MM-DD');
      this.weekStart    = moment().startOf('isoWeek');
      this.viewMode     = 'W';
      this.rangeStart   = today;
      this.rangeEnd     = today;
      this.selectedDate = today;
      this.hourStats    = {};
      this.loadData().then(() => {
        this.loadHours(today);
        const chip = this.activeHourChip;
        this.$emit('daySelected', { date: today, dateTo: today, type: chip.key, fromParam: chip.fromParam, toParam: chip.toParam });
      });
    },

    applyCurrentFilter() {
      const chip = this.activeHourChip;
      if (chip && this.selectedDate) this.$emit("daySelected", { date: this.selectedDate, type: chip.key, fromParam: chip.fromParam, toParam: chip.toParam });
    },

    async loadData() {
      this.gridLoading = true;
      const { from, to } = this.viewRange;
      const fromStr = from.format("YYYY-MM-DD");
      const toStr   = to.format("YYYY-MM-DD");
      const newStats = {};
      await Promise.all(CHIPS.map(chip =>
        apiClientCreator(`client/stats/calendar/${chip.endpoint}`)
          .get("", { params: { [chip.fromParam]: fromStr, [chip.toParam]: toStr, ...this.filterParams } })
          .then(res => {
            if (Array.isArray(res.data)) {
              res.data.forEach(item => {
                if (item.date && item.count !== undefined) {
                  if (!newStats[item.date]) newStats[item.date] = {};
                  newStats[item.date][chip.key] = item.count;
                }
              });
            }
          })
          .catch(() => {})
      ));
      this.dayStats    = newStats;
      this.gridLoading = false;
      if (this.viewMode === "D") this.loadHours(this.dayStart.format("YYYY-MM-DD"));
    },

    loadWeek() { this.loadData(); },

    async loadHours(date) {
      const chip = this.activeHourChip;
      if (!chip) return;
      this.hoursLoading = true;
      this.hourStats = {};
      try {
        const res = await apiClientCreator(`client/stats/calendar/hours_stats/${chip.hoursEndpoint}`)
          .get("", { params: { [chip.fromParam]: date, [chip.toParam]: date, ...this.filterParams } });
        const stats = {};
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            let hh = null;
            if (item.hour !== undefined) {
              hh = String(Math.floor(Number(item.hour))).padStart(2, '0');
            } else if (item.date) {
              // Handle: "08:00:00", "2026-04-10 08:00:00", "2026-04-10T08:00:00Z"
              const timeStr = item.date.includes('T') ? item.date.split('T')[1]
                            : item.date.includes(' ') ? item.date.split(' ')[1]
                            : item.date;
              if (timeStr) hh = String(parseInt(timeStr.split(':')[0], 10)).padStart(2, '0');
            }
            if (hh !== null) stats[hh] = (stats[hh] || 0) + (item.count || 0);
          });
        }
        this.hourStats = stats;
      } catch (e) {}
      this.hoursLoading = false;
    },

    dayCount(date, type) { return (this.dayStats[date] && this.dayStats[date][type]) || 0; },
    dayTotal(date) {
      if (!this.dayStats[date]) return 0;
      return this.visibleTypes.reduce((s, t) => s + (this.dayStats[date][t] || 0), 0);
    },
    stackH(date, type) {
      const cnt = this.dayCount(date, type);
      if (!cnt) return 0;
      let max = 0;
      this.weekDays.forEach(d => { const v = this.dayCount(d.date, type); if (v > max) max = v; });
      return max ? Math.max(8, Math.round((cnt / max) * 44)) : 0;
    },
    hourCount(h) { return this.hourStats[String(h).padStart(2, "0")] || 0; },
    hourPct(h)   { return Math.round((this.hourCount(h) / this.maxHourCount) * 100); },
    aggPct(count, max) { return Math.round((count / (max || 1)) * 90); },
  },
};
</script>

<style scoped>
.sc-wrap {
  background: var(--c1-card, #FFF);
  font-family: 'DM Sans', -apple-system, sans-serif;
  font-size: 12px; color: #1A1A1A;
  display: flex; flex-direction: column;
  overscroll-behavior: contain;
}

/* NAV ROW */
.sc-wnav-row {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 8px;
  border-bottom: 1px solid #E4E3DF;
  background: #fff; flex-shrink: 0;
  min-height: 36px;
}
.sc-nav-label-wrap {
  flex: 1; display: flex; align-items: center; gap: 4px;
  min-width: 0; overflow: hidden;
}
.sc-nav-right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.sc-wlabel {
  font-size: 11px; font-weight: 700; color: #1A1A1A;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sc-wbtn {
  width: 22px; height: 22px; border-radius: 5px; border: 1px solid #E4E3DF;
  background: #FAFAF8; cursor: pointer; font-size: 15px; line-height: 1;
  display: grid; place-items: center; color: #6B6963; padding: 0; flex-shrink: 0; transition: background .1s;
}
.sc-wbtn:hover { background: #EEEDEA; }

/* Range pills — shown inside the nav label area */
.sc-range-pill {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700;
  background: #F0ECFF; color: #7C5CFC; white-space: nowrap; flex-shrink: 0;
}
.sc-range-pill--start { background: #7C5CFC; color: #fff; }
.sc-range-pill--end   { background: #7C5CFC; color: #fff; }
.sc-range-pill--empty { background: #F0ECFF; color: #a78bfa; border: 1.5px dashed #c4b5fd; }
.sc-range-arrow { font-size: 11px; font-weight: 700; color: #a78bfa; flex-shrink: 0; }
.sc-range-x {
  width: 18px; height: 18px; border-radius: 50%; border: none;
  background: #E4E3DF; color: #6B6963; font-size: 10px; font-weight: 700;
  cursor: pointer; display: grid; place-items: center; padding: 0; flex-shrink: 0;
  transition: background .1s;
}
.sc-range-x:hover { background: #ef4444; color: #fff; }

/* View mode custom dropdown */
.sc-view-wrap {
  position: relative; display: flex; align-items: center; flex-shrink: 0;
}
.sc-view-btn {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; color: #1A1A1A;
  background: #FAFAF8; border: 1px solid #E4E3DF; border-radius: 6px;
  padding: 3px 22px 3px 8px; cursor: pointer; position: relative;
  transition: border-color .1s; user-select: none; white-space: nowrap;
}
.sc-view-btn:hover, .sc-view-btn.open { border-color: #7C5CFC; background: #F7F6FF; }
.sc-view-arr {
  position: absolute; right: 5px; top: 50%; transform: translateY(-50%);
  width: 10px; height: 10px; pointer-events: none; stroke: #A09E98;
  transition: transform .15s;
}
.sc-view-dd {
  background: #fff; border: 1px solid #E4E3DF; border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,.12); min-width: 110px; padding: 4px 0 2px; overflow: hidden;
}
.sc-dd-fade-enter-active, .sc-dd-fade-leave-active { transition: opacity .12s, transform .12s; }
.sc-dd-fade-enter, .sc-dd-fade-leave-to { opacity: 0; transform: translateY(-4px); }
.sc-view-opt {
  display: flex; align-items: center; justify-content: space-between;
  padding: 7px 14px; font-size: 12px; font-weight: 500; color: #1A1A1A;
  cursor: pointer; transition: background .1s;
}
.sc-view-opt:hover { background: #F3F0FF; }
.sc-view-opt.on { background: rgba(124,92,252,.06); font-weight: 700; color: #7C5CFC; }

/* Custom date inputs (inside nav row when C mode) */
.sc-date-input {
  border: 1px solid #E4E3DF; border-radius: 6px;
  padding: 3px 5px; font-size: 10px; font-family: 'DM Sans', sans-serif;
  font-weight: 600; color: #1A1A1A; background: #FAFAF8; outline: none; cursor: pointer;
  width: 96px; flex-shrink: 0;
}
.sc-date-input:focus { border-color: #7C5CFC; }
.sc-range-sep { font-size: 10px; color: #A09E98; font-weight: 700; flex-shrink: 0; }
.sc-range-apply {
  padding: 3px 8px; border-radius: 5px; border: none;
  background: #7C5CFC; color: #fff; font-size: 10px; font-weight: 800;
  cursor: pointer; flex-shrink: 0; transition: opacity .12s;
}
.sc-range-apply:hover { opacity: .85; }

/* REFRESH */
.sc-refresh-btn {
  width: 22px; height: 22px; display: grid; place-items: center; cursor: pointer;
  border-radius: 5px; border: 1px solid #E4E3DF; background: #FAFAF8; transition: background .1s; flex-shrink: 0;
}
.sc-refresh-btn:hover { background: #EEEDEA; }
.sc-refresh-btn.spin svg { animation: sc-spin .8s linear infinite; }
.sc-refresh-btn svg { width: 12px; height: 12px; stroke: #A09E98; fill: none; stroke-width: 2; }

/* CHIPS ROW */
.sc-chips-row {
  display: flex; gap: 5px; padding: 6px 10px;
  border-bottom: 1px solid #E4E3DF; overflow-x: auto;
  background: #fff; scrollbar-width: none; flex-shrink: 0;
}
.sc-chips-row::-webkit-scrollbar { display: none; }
.sc-chip {
  padding: 3px 10px; border-radius: 20px; font-size: 10px; font-weight: 700;
  cursor: pointer; border: 1.5px solid; transition: all .12s;
  user-select: none; white-space: nowrap; flex-shrink: 0;
}
.sc-chip:hover { opacity: .8; }

/* DAY VIEW */
.sc-day-view { padding: 10px; border-bottom: 1px solid #E4E3DF; flex-shrink: 0; transition: opacity .2s; }
.sc-day-view.loading { opacity: .4; pointer-events: none; }
.sc-day-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.sc-day-kpi { text-align: center; background: #FAFAF8; border: 1px solid #EEEDEA; border-radius: 8px; padding: 8px 4px; }
.sc-day-kpi-val { font-size: 20px; font-weight: 800; line-height: 1; }
.sc-day-kpi-lbl { font-size: 9px; font-weight: 600; color: #A09E98; margin-top: 2px; text-transform: uppercase; letter-spacing: .4px; }

/* WEEK GRID */
.sc-grid { display: grid; grid-template-columns: repeat(7, 1fr); border-bottom: 1px solid #E4E3DF; transition: opacity .2s; flex-shrink: 0; }
.sc-grid.loading { opacity: .4; pointer-events: none; }
.sc-day {
  border-right: 1px solid #EEEDEA; padding: 5px 3px 3px; cursor: pointer;
  display: flex; flex-direction: column; align-items: center;
  min-height: 80px; transition: background .1s; position: relative;
}
.sc-day:last-child { border-right: none; }
.sc-day:hover { background: #FAFAF8; }
.sc-day.selected { background: #F0ECFF; }
.sc-day.today { background: #FFFBE8; }
/* Range styles */
.sc-day.sc-rs, .sc-day.sc-re { background: #7C5CFC !important; }
.sc-day.sc-rs .sc-dow, .sc-day.sc-re .sc-dow,
.sc-day.sc-rs .sc-dd, .sc-day.sc-re .sc-dd,
.sc-day.sc-rs .sc-day-total, .sc-day.sc-re .sc-day-total { color: #fff !important; }
.sc-day.sc-in-range { background: #EDE9FF; }
.sc-day-hdr { text-align: center; margin-bottom: 3px; }
.sc-dow { display: block; font-size: 7px; font-weight: 700; text-transform: uppercase; color: #A09E98; letter-spacing: .4px; }
.sc-dd { display: block; font-size: 15px; font-weight: 700; color: #1A1A1A; line-height: 1.1; }
.sc-dd-today { color: #7C5CFC; }
.sc-day-cnts { display: flex; flex-direction: column; gap: 2px; width: 100%; padding: 2px 3px; margin-top: 2px; flex: 1; }
.sc-cnt-row { display: flex; align-items: center; gap: 3px; cursor: pointer; border-radius: 3px; padding: 1px 2px; transition: background .1s; }
.sc-cnt-row:hover { background: rgba(0,0,0,.08); }
.sc-cnt-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.sc-cnt-num { font-size: 10px; font-weight: 800; line-height: 1; }
.sc-day.sc-rs .sc-cnt-num, .sc-day.sc-re .sc-cnt-num { color: rgba(255,255,255,.9) !important; }
.sc-day.sc-rs .sc-cnt-dot, .sc-day.sc-re .sc-cnt-dot { background: rgba(255,255,255,.7) !important; }

/* MONTH GRID */
.sc-mgrid { display: grid; grid-template-columns: repeat(7, 1fr); border-bottom: 1px solid #E4E3DF; transition: opacity .2s; flex-shrink: 0; }
.sc-mgrid.loading { opacity: .4; pointer-events: none; }
.sc-mhdr { text-align: center; font-size: 8px; font-weight: 700; color: #A09E98; padding: 5px 2px; border-bottom: 1px solid #EEEDEA; background: #FAFAF8; }
.sc-mcell {
  border-right: 1px solid #EEEDEA; border-bottom: 1px solid #EEEDEA;
  padding: 4px 3px 3px; min-height: 50px; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; transition: background .1s;
}
.sc-mcell:nth-child(7n) { border-right: none; }
.sc-mcell:hover { background: #FAFAF8; }
.sc-mcell-empty { cursor: default; background: #FAFAF8; }
.sc-mcell-today { background: #FFFBE8; }
.sc-mcell-sel { background: #F0ECFF; }
/* Month range styles */
.sc-mcell.sc-rs, .sc-mcell.sc-re { background: #7C5CFC !important; }
.sc-mcell.sc-rs .sc-mdd, .sc-mcell.sc-re .sc-mdd,
.sc-mcell.sc-rs .sc-mday-total, .sc-mcell.sc-re .sc-mday-total { color: #fff !important; }
.sc-mcell.sc-in-range { background: #EDE9FF; }
.sc-mdd { font-size: 10px; font-weight: 700; color: #1A1A1A; line-height: 1.2; }
.sc-mdd-today { color: #7C5CFC; }
.sc-mdots { display: flex; flex-wrap: wrap; gap: 2px; justify-content: center; margin-top: 3px; }
.sc-mdot { width: 6px; height: 6px; border-radius: 50%; cursor: pointer; transition: transform .1s; }
.sc-mdot:hover { transform: scale(1.5); }
.sc-mday-total { font-size: 7px; font-weight: 600; color: #A09E98; margin-top: 2px; }

/* QUARTER — 4 boxes */
.sc-q-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #E4E3DF; flex-shrink: 0; transition: opacity .2s; }
.sc-q-grid.loading { opacity: .4; pointer-events: none; }
.sc-q-box { padding: 12px 12px 10px; cursor: pointer; transition: background .1s; border-right: 1px solid #EEEDEA; border-bottom: 1px solid #EEEDEA; display: flex; flex-direction: column; gap: 6px; }
.sc-q-box:nth-child(2n) { border-right: none; }
.sc-q-box:nth-child(3), .sc-q-box:nth-child(4) { border-bottom: none; }
.sc-q-box:hover { background: #FAFAF8; }
.sc-q-top { display: flex; align-items: baseline; gap: 6px; }
.sc-q-label { font-size: 18px; font-weight: 800; color: #1A1A1A; }
.sc-q-range { font-size: 10px; color: #A09E98; font-weight: 600; }
.sc-q-bars { display: flex; flex-direction: column; gap: 3px; flex: 1; }
.sc-q-bar { height: 13px; border-radius: 3px; min-width: 4px; display: flex; align-items: center; padding-left: 5px; }
.sc-q-empty { font-size: 10px; color: #d1d5db; font-style: italic; }
.sc-q-total { font-size: 11px; font-weight: 700; color: #6B6963; }

/* AGG LIST (Year + Custom) */
.sc-agg-list { display: flex; flex-direction: column; border-bottom: 1px solid #E4E3DF; transition: opacity .2s; flex-shrink: 0; max-height: 280px; overflow-y: auto; }
.sc-agg-list.loading { opacity: .4; pointer-events: none; }
.sc-agg-row { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-bottom: 1px solid #EEEDEA; cursor: pointer; transition: background .1s; flex-shrink: 0; }
.sc-agg-row:last-child { border-bottom: none; }
.sc-agg-row:hover { background: #FAFAF8; }
.sc-agg-lbl { font-size: 10px; font-weight: 700; color: #6B6963; white-space: nowrap; width: 80px; flex-shrink: 0; font-family: 'JetBrains Mono', monospace; }
.sc-agg-bars { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.sc-agg-bar { height: 12px; border-radius: 3px; min-width: 4px; display: flex; align-items: center; padding-left: 4px; }
.sc-agg-val { font-size: 8px; color: #fff; font-weight: 700; }

/* HOURLY */
.sc-hourly-full { padding: 10px 12px 12px; border-bottom: 1px solid #E4E3DF; flex-shrink: 0; }
.sc-no-day { font-size: 9px; color: #A09E98; text-align: center; padding: 12px; font-style: italic; flex-shrink: 0; }
.sc-panel-hdr { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.sc-panel-date { font-size: 11px; font-weight: 700; color: #1A1A1A; white-space: nowrap; }
.sc-panel-type { font-size: 11px; font-weight: 800; text-transform: uppercase; white-space: nowrap; }
.sc-filter-btn { margin-left: auto; padding: 4px 10px; border-radius: 4px; border: none; background: #7C5CFC; color: #fff; font-size: 9px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.sc-filter-btn:hover { opacity: .85; }
.sc-hourly-bars { display: flex; flex-direction: column; gap: 3px; }
.sc-hbar { display: flex; align-items: center; gap: 5px; border-radius: 4px; padding: 2px 3px; transition: background .1s; }
.sc-hbar-active { cursor: pointer; }
.sc-hbar-active:hover { background: #F4F3F0; }
.sc-hlbl { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: #A09E98; width: 30px; flex-shrink: 0; }
.sc-htrack { flex: 1; height: 14px; background: #EEEDEA; border-radius: 3px; overflow: hidden; }
.sc-hfill { height: 100%; border-radius: 3px; display: flex; align-items: center; padding-left: 4px; transition: width .3s; }
.sc-hval { font-size: 8px; color: #fff; font-weight: 700; }
.sc-hcount { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: #6B6963; width: 18px; text-align: right; flex-shrink: 0; }
.sc-h-loading { display: flex; justify-content: center; align-items: center; height: 60px; }
.sc-mini-spin { width: 18px; height: 18px; border: 2px solid #E4E3DF; border-top-color: #7C5CFC; border-radius: 50%; animation: sc-spin .8s linear infinite; }
@keyframes sc-spin { to { transform: rotate(360deg); } }
</style>
